import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * Re-renders one recommendation card through Shopify's granular option_values API.
 * @typedef {Object} ProductRefs
 * @property {HTMLElement} card
 * @property {HTMLElement} selectionStatus
 * @extends {Component<ProductRefs>}
 */
class CartProduct extends Component {
  requiredRefs = ['card', 'selectionStatus'];
  #listeners;
  #request;
  #busy = false;
  #committed = [];
  #plan = '';

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#listeners?.abort();
    this.#request?.abort();
    this.#request = undefined;
    this.#busy = false;
  }

  #options() { return [...this.querySelectorAll('[data-se-cart-option]')]; }
  #button() { return this.querySelector('[data-se-cart-add]'); }
  #adding() { return this.#button()?.getAttribute('aria-busy') === 'true'; }

  #bind() {
    this.#cleanup();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#committed = this.#options().map((select) => select.value);
    this.#plan = this.querySelector('[data-se-cart-plan]')?.value || '';
    this.addEventListener('change', this.#change, options);
    this.addEventListener('submit', (event) => {
      if (!this.#busy && this.#canAdd()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { ...options, capture: true });
    this.refs.card.removeAttribute('aria-busy');
    this.#sync();
  }

  #canAdd() {
    const plan = this.querySelector('[data-se-cart-plan]');
    const planMissing = this.querySelector('input[name="selling_plan"][required]');
    return this.refs.card.dataset.variantAvailable === 'true' && Boolean(this.refs.card.dataset.variantId) && !planMissing && (!plan?.required || Boolean(plan.value));
  }

  #sync() {
    const button = this.#button();
    if (!button || this.#adding()) return;
    button.disabled = this.#busy || !this.#canAdd();
    const quickAdd = this.querySelector('se-quick-add');
    if (quickAdd && typeof quickAdd.updatedCallback === 'function') quickAdd.updatedCallback();
    else {
      const card = this.refs.card;
      customElements.whenDefined('se-quick-add').then(() => {
        if (this.isConnected && this.refs.card === card) this.#sync();
      });
    }
  }

  #restore() {
    this.#options().forEach((select, index) => { select.value = this.#committed[index]; });
    const plan = this.querySelector('[data-se-cart-plan]');
    if (plan) plan.value = this.#plan;
  }

  #change = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.matches('[data-se-cart-option], [data-se-cart-plan]')) return;
    if (this.#adding()) { this.#restore(); return; }
    if (select.matches('[data-se-cart-plan]')) {
      if (this.#busy) { select.value = this.#plan; return; }
      this.#plan = select.value;
      const price = select.selectedOptions[0]?.dataset.price;
      if (price) this.querySelector('[data-se-cart-price]').textContent = price;
      this.refs.selectionStatus.textContent = select.required && !select.value ? select.options[0].textContent : '';
      this.#sync();
      return;
    }
    this.#load(select);
  };

  async #load(changedSelect) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#busy = true;
    this.#sync();
    this.refs.card.setAttribute('aria-busy', 'true');
    this.refs.selectionStatus.textContent = this.dataset.updating;
    const selected = this.#options().map((select) => select.value);
    const position = changedSelect.dataset.position;
    const url = new URL(this.dataset.productUrl, window.location.href);
    url.searchParams.delete('variant');
    url.searchParams.delete('selling_plan');
    url.searchParams.set('option_values', selected.join(','));
    url.searchParams.set('section_id', 'se-cart-product');
    if (this.#plan) url.searchParams.set('selling_plan', this.#plan);
    try {
      if (url.origin !== window.location.origin) throw new Error('Invalid product URL');
      const response = await fetch(url, { credentials: 'same-origin', signal: request.signal });
      if (!response.ok) throw new Error(`Cart variant: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!this.isConnected || request.signal.aborted || this.#request !== request) return;
      const product = doc.querySelector('se-cart-product');
      const card = product?.querySelector('.se-cart-card');
      if (!card || product.dataset.productId !== this.dataset.productId) throw new Error('Invalid product response');
      const linkURL = new URL(this.dataset.productUrl, window.location.href);
      linkURL.searchParams.delete('option_values');
      if (card.dataset.variantId) linkURL.searchParams.set('variant', card.dataset.variantId);
      const plan = card.querySelector('[data-se-cart-plan]')?.value;
      if (plan) linkURL.searchParams.set('selling_plan', plan);
      else linkURL.searchParams.delete('selling_plan');
      card.querySelector('[data-se-cart-product-link]')?.setAttribute('href', linkURL.pathname + linkURL.search);
      const active = document.activeElement === changedSelect;
      // A card fragment is morphed in place; the containing drawer and carousel retain their state.
      morph(this.refs.card, card, { childrenOnly: false });
      this.updatedCallback();
      if (active) this.#options().find((select) => select.dataset.position === position)?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError' || !this.isConnected || this.#request !== request) return;
      this.#restore();
      this.refs.selectionStatus.textContent = this.dataset.error;
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        this.#busy = false;
        if (this.isConnected) {
          this.refs.card.removeAttribute('aria-busy');
          this.#sync();
        }
      }
    }
  }
}

if (!customElements.get('se-cart-product')) customElements.define('se-cart-product', CartProduct);
